"use client";

import type { Variants } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import React, { forwardRef, useCallback, useImperativeHandle } from "react";

import { cn } from "@/lib/utils";
import { useHoverResetGuard } from "./useHoverResetGuard";

export interface ChartSplineIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface ChartSplineIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  /** e.g. `inherit` from sidebar route row so `currentColor` follows the nav icon color */
  color?: string;
}

const VARIANTS: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      delay: 0.15,
      duration: 0.3,
      opacity: { delay: 0.1 },
    },
  },
};

const ChartSplineIcon = forwardRef<ChartSplineIconHandle, ChartSplineIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 20,
      color,
      style,
      ...props
    },
    ref
  ) => {
    const controls = useAnimation();
    const { withLockedHoverReset, releaseHoverResetLock, shouldApplyHoverReset } = useHoverResetGuard();

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () =>
          void withLockedHoverReset(async () => {
            await controls.start("animate");
          }),
        stopAnimation: () => {
          releaseHoverResetLock();
          void controls.start("normal");
        },
      }),
      [controls, releaseHoverResetLock, withLockedHoverReset]
    );

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e);
        void controls.start("animate");
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e);
        if (!shouldApplyHoverReset()) return;
        void controls.start("normal");
      },
      [controls, onMouseLeave, shouldApplyHoverReset]
    );

    return (
      <div
        className={cn(className)}
        style={{ color: color ?? undefined, ...style }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 3v16a2 2 0 0 0 2 2h16" />
          <motion.path
            animate={controls}
            d="M7 16c.5-2 1.5-7 4-7 2 0 2 3 4 3 2.5 0 4.5-5 5-7"
            initial={{ pathLength: 1, opacity: 1 }}
            variants={VARIANTS}
          />
        </svg>
      </div>
    );
  }
);

ChartSplineIcon.displayName = "ChartSplineIcon";

export { ChartSplineIcon };
